/**
 * Tenant Config Hooks (Sprint 171 D-006 / Wave C4)
 *
 * React Query hooks for the per-tenant configuration editors on Tenant
 * Detail. Each wraps `tenantConfigApi` in a query (GET) and mutation (PUT)
 * with cache invalidation, and every hook addresses the tenant by explicit
 * id: a platform administrator edits a named FI, never the tenant the JWT
 * happens to imply. The implicit-tenant pair had no caller and was deleted
 * by operator ruling (2026-09-27, unwired-deletions).
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  tenantConfigApi,
  type TenantNotificationConfig,
  type TenantSlaConfig,
  type TenantReviewerConfig,
} from '../api/services/tenant-config';

const TENANT_CONFIG_ROOT = 'admin';
const TENANT_CONFIG_KEY = 'tenant-config';
const NOTIFICATIONS_SLICE = 'notifications';
const SLA_SLICE = 'sla';
const REVIEWERS_SLICE = 'reviewers';

const tenantConfigKeys = {
  all: [TENANT_CONFIG_ROOT, TENANT_CONFIG_KEY] as const,
  notifications: () =>
    [TENANT_CONFIG_ROOT, TENANT_CONFIG_KEY, NOTIFICATIONS_SLICE] as const,
  notificationsByTenant: (tenantId: string) =>
    [TENANT_CONFIG_ROOT, TENANT_CONFIG_KEY, NOTIFICATIONS_SLICE, tenantId] as const,
  slaByTenant: (tenantId: string) =>
    [TENANT_CONFIG_ROOT, TENANT_CONFIG_KEY, SLA_SLICE, tenantId] as const,
  reviewersByTenant: (tenantId: string) =>
    [TENANT_CONFIG_ROOT, TENANT_CONFIG_KEY, REVIEWERS_SLICE, tenantId] as const,
};

type ApiErrorEnvelope = {
  message?: string;
  error?: { code?: string; message?: string; details?: unknown } | string;
};

/**
 * Normalize backend error envelopes (`{ error: { code, message } }`,
 * `{ error: string }`, plain strings) into a single `Error.message` so the
 * UI can surface 400-validation messages directly to the operator.
 */
function describeError(err: unknown, fallback: string): string {
  if (!err) return fallback;
  if (err instanceof Error) return err.message;
  const env = err as ApiErrorEnvelope;
  if (env.message) return env.message;
  if (typeof env.error === 'string') return env.error;
  if (env.error && typeof env.error === 'object' && env.error.message) {
    return env.error.message;
  }
  if (typeof err === 'string') return err;
  return fallback;
}

/**
 * Query hook for a specific tenant's full notification configuration (channels,
 * digest settings). Used by platform-admin tenant detail page. Gate with
 * `enabled` when tenantId is still undefined.
 */
export function useTenantNotificationConfigById(tenantId: string | undefined) {
  return useQuery<TenantNotificationConfig, Error>({
    queryKey: tenantConfigKeys.notificationsByTenant(tenantId ?? ''),
    queryFn: () => tenantConfigApi.getTenantNotificationConfig(tenantId ?? ''),
    enabled: Boolean(tenantId),
  });
}

/**
 * Mutation hook for replacing a specific tenant's full notification config.
 * Spec is full-replace; callers MUST send the complete config including all
 * existing channels. On success, invalidates the per-tenant query.
 */
export function useUpdateTenantNotificationConfigById(tenantId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation<TenantNotificationConfig, Error, TenantNotificationConfig>({
    mutationFn: async (body) => {
      try {
        return await tenantConfigApi.putTenantNotificationConfig(tenantId ?? '', body);
      } catch (err) {
        throw new Error(describeError(err, 'Failed to update notification config'));
      }
    },
    onSuccess: () => {
      if (tenantId) {
        queryClient.invalidateQueries({
          queryKey: tenantConfigKeys.notificationsByTenant(tenantId),
        });
      }
    },
  });
}

// ---------------------------------------------------------------------------
// SLA configuration
// ---------------------------------------------------------------------------

/**
 * Query hook for the SLA configuration of a specific tenant. Drives
 * green/yellow/red task age bands and digest scheduling. Gate with
 * `enabled` when tenantId is undefined.
 */
export function useTenantSlaConfig(tenantId: string | undefined) {
  return useQuery<TenantSlaConfig, Error>({
    queryKey: tenantConfigKeys.slaByTenant(tenantId ?? ''),
    queryFn: () => tenantConfigApi.getTenantSlaConfig(tenantId ?? ''),
    enabled: Boolean(tenantId),
  });
}

/**
 * Mutation hook for replacing the SLA config. Server validates
 * greenHours < yellowHours and surfaces a 400 with the validation
 * message which is propagated to the caller via Error.message.
 */
export function useUpdateTenantSlaConfig(tenantId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation<TenantSlaConfig, Error, TenantSlaConfig>({
    mutationFn: async (body) => {
      try {
        return await tenantConfigApi.putTenantSlaConfig(tenantId ?? '', body);
      } catch (err) {
        throw new Error(describeError(err, 'Failed to update SLA config'));
      }
    },
    onSuccess: () => {
      if (tenantId) {
        queryClient.invalidateQueries({
          queryKey: tenantConfigKeys.slaByTenant(tenantId),
        });
      }
    },
  });
}

// ---------------------------------------------------------------------------
// Reviewer assignment configuration
// ---------------------------------------------------------------------------

/**
 * Query hook for the reviewer-assignment configuration of a specific
 * tenant. Drives pledge routing (default_only / round_robin /
 * by_package_size). Gate with `enabled` when tenantId is undefined.
 */
export function useTenantReviewerConfig(tenantId: string | undefined) {
  return useQuery<TenantReviewerConfig, Error>({
    queryKey: tenantConfigKeys.reviewersByTenant(tenantId ?? ''),
    queryFn: () => tenantConfigApi.getTenantReviewerConfig(tenantId ?? ''),
    enabled: Boolean(tenantId),
  });
}

/**
 * Mutation hook for replacing the reviewer-assignment config. Spec is
 * full-replace; the server enforces rule-specific validation:
 *  - round_robin requires non-empty roundRobinReviewerIds
 *  - by_package_size requires complete sizeThresholds
 */
export function useUpdateTenantReviewerConfig(tenantId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation<TenantReviewerConfig, Error, TenantReviewerConfig>({
    mutationFn: async (body) => {
      try {
        return await tenantConfigApi.putTenantReviewerConfig(tenantId ?? '', body);
      } catch (err) {
        throw new Error(describeError(err, 'Failed to update reviewer config'));
      }
    },
    onSuccess: () => {
      if (tenantId) {
        queryClient.invalidateQueries({
          queryKey: tenantConfigKeys.reviewersByTenant(tenantId),
        });
      }
    },
  });
}
